import { useEffect, useState } from "react";
import { Mail, X, Gift } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "fs_subscribe_popup_v1";

export default function SubscriptionPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    try {
      const flag = localStorage.getItem(STORAGE_KEY);
      if (flag) return; // already shown / subscribed / dismissed
    } catch {}

    let timer = null;
    let shown = false;

    const onScroll = () => {
      const pct = (window.scrollY + window.innerHeight) / document.body.scrollHeight;
      if (!shown && pct > 0.5) {
        shown = true;
        setOpen(true);
        window.removeEventListener("scroll", onScroll);
      }
    };

    timer = setTimeout(() => {
      if (!shown) {
        shown = true;
        setOpen(true);
      }
    }, 30000); // 30 seconds

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "dismissed"); } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      return toast.error("Ingresa un correo válido");
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "popup" }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      try { localStorage.setItem(STORAGE_KEY, "subscribed"); } catch {}
      toast.success("¡Estás dentro!");
      setTimeout(() => setOpen(false), 2500);
    } catch {
      toast.error("Hubo un problema. Intenta más tarde.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={dismiss}
      data-testid="subscription-popup-backdrop"
    >
      <div
        className="relative bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="subscription-popup"
      >
        <button
          onClick={dismiss}
          aria-label="Cerrar"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center text-neutral-600"
          data-testid="subscription-popup-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="bg-gradient-to-br from-[#0a0a0a] to-[#1a0a0a] text-white px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-500/20 mb-3">
            <Gift className="w-6 h-6 text-orange-400" />
          </div>
          <h3 className="text-2xl font-black section-title">¿Te gusta lo que ves?</h3>
          <p className="mt-2 text-white/70 text-sm">
            Recibe promociones exclusivas, nuevos proyectos y tips FlexShade directo a tu correo.
          </p>
        </div>

        {done ? (
          <div className="p-6 text-center">
            <p className="text-green-600 font-bold text-lg">¡Gracias por suscribirte!</p>
            <p className="text-sm text-neutral-500 mt-1">Revisa tu correo, te enviamos la confirmación.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            <label className="text-xs font-semibold text-neutral-700">Tu email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                required
                placeholder="tucorreo@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input w-full pl-9"
                data-testid="popup-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 rounded-lg text-sm font-bold disabled:opacity-60"
              data-testid="popup-submit"
            >
              {loading ? "Suscribiendo..." : "Quiero recibir novedades"}
            </button>
            <p className="text-[11px] text-neutral-400 text-center">
              Podrás darte de baja en cualquier momento desde el correo.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
